import Icon from 'components/_UI/Icon';
import StoreBadges from 'components/appStores/StoreBadges';
import CONTACT from 'constants/contact';
import SOCIAL_MEDIA from 'constants/socialMedia';
import { useAuth } from 'context/auth/Auth';
import React from 'react'
import { Link } from 'react-router-dom';
import paths from 'routes/paths';
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';

// Formulário antigo de interesse em criar eventos. Continua sendo o destino
// para quem já está logado, porque /quero-ser-parceiro só existe deslogado.
const PRODUCER_INTEREST_FORM = 'https://docs.google.com/forms/d/192P_cUM7SJVSpGkod0FFf5lqkSgbmi5TQrA15djApiw/';

const SOCIALS = [
    { name: 'instagram', label: 'Instagram', href: SOCIAL_MEDIA.instagram },
    { name: 'facebook', label: 'Facebook', href: SOCIAL_MEDIA.facebook },
    { name: 'linkedin', label: 'LinkedIn', href: SOCIAL_MEDIA.linkedin },
];

export default function Footer() {
    const { isAuth } = useAuth();

    const sections = [
        {
            title: 'Descubra',
            links: [
                { label: 'Eventos', to: paths.events.home },
                { label: 'Estabelecimentos', to: paths.establishments.home },
            ],
        },
        {
            title: 'Para produtores',
            links: [
                isAuth
                    ? { label: 'Criar eventos no Whizr', href: PRODUCER_INTEREST_FORM }
                    : { label: 'Criar eventos no Whizr', to: paths.partner.createAccount },
            ],
        },
        {
            title: 'Ajuda',
            links: [
                { label: 'Central de ajuda', href: CONTACT.whatsapp },
                { label: 'Fale conosco', to: paths.contact },
                { label: CONTACT.email, href: `mailto:${CONTACT.email}`, sameTab: true },
            ],
        },
    ];

    const legalLinks = [
        { label: 'Termos de uso', to: paths.terms.useTerms },
        { label: 'Política de privacidade', to: paths.terms.policiePrivacy },
        { label: 'Exclusão de conta', to: paths.terms.deleteAccount },
    ];

    return (
        <FooterRoot>
            <Inner>
                <Brand>
                    <LogoLink to={paths.home} aria-label='Whizr, página inicial'>
                        <Icon name='whizr-inline-logo-dark' />
                    </LogoLink>
                    <Tagline>Eventos e lugares da sua cidade, num só lugar.</Tagline>

                    <SocialList aria-label='Whizr nas redes sociais'>
                        {SOCIALS.map((social) => (
                            <li key={social.name}>
                                <SocialLink
                                    href={social.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    aria-label={`${social.label} (abre em nova aba)`}
                                >
                                    <Icon name={social.name} color='currentColor' />
                                </SocialLink>
                            </li>
                        ))}
                    </SocialList>
                </Brand>

                <Nav aria-label='Rodapé'>
                    {sections.map((section) => (
                        <Section key={section.title}>
                            <SectionTitle>{section.title}</SectionTitle>
                            <LinkList>
                                {section.links.map((link) => (
                                    <li key={link.label}>
                                        <FooterLink link={link} />
                                    </li>
                                ))}
                            </LinkList>
                        </Section>
                    ))}
                </Nav>

                <Aside>
                    <Section>
                        <SectionTitle>Baixe o app</SectionTitle>
                        <BadgesSlot>
                            <StoreBadges source='footer' />
                        </BadgesSlot>
                    </Section>

                    <Section>
                        <SectionTitle>Acelerado por</SectionTitle>
                        <SealLink
                            href='https://www.inovativa.online/'
                            target='_blank'
                            rel='noopener noreferrer'
                        >
                            <img
                                src='/assets/images/inovativa-seal.png'
                                alt='InovAtiva Brasil (abre em nova aba)'
                                width={80}
                                loading='lazy'
                            />
                        </SealLink>
                    </Section>
                </Aside>
            </Inner>

            <Bottom>
                <BottomInner>
                    <Legal>
                        <span>© {new Date().getFullYear()} Whizr Plataforma de Conteúdo LTDA</span>
                        <span>CNPJ 54.729.749/0001-01 · Todos os direitos reservados</span>
                    </Legal>

                    <LegalNav aria-label='Documentos legais'>
                        <LegalList>
                            {legalLinks.map((link) => (
                                <li key={link.label}>
                                    <FooterLink link={link} small />
                                </li>
                            ))}
                        </LegalList>
                    </LegalNav>
                </BottomInner>
            </Bottom>
        </FooterRoot>
    )
}

/**
 * Rota interna abre na mesma aba (via router); link externo abre em nova aba
 * e avisa leitores de tela disso.
 */
function FooterLink({ link, small }) {
    if (link.to) {
        return <TextLink as={Link} to={link.to} $small={small}>{link.label}</TextLink>;
    }

    if (link.sameTab) {
        return <TextLink href={link.href} $small={small}>{link.label}</TextLink>;
    }

    return (
        <TextLink href={link.href} target='_blank' rel='noopener noreferrer' $small={small}>
            {link.label}
            <VisuallyHidden> (abre em nova aba)</VisuallyHidden>
        </TextLink>
    );
}

// #52525B sobre #F5F5F7 dá ~7:1 (AAA); títulos em nightDark.
const TEXT_MUTED = '#52525B';
const SURFACE = '#F5F5F7';
const SURFACE_BOTTOM = '#EBEBF1';
const FOCUS = '#1D4ED8';

const focusRing = `
    &:focus-visible {
        outline: 2px solid ${FOCUS};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const FooterRoot = styled.footer`
    width: 100%;
    background-color: ${SURFACE};
    border-top: 1px solid #E4E4E7;
    color: ${TEXT_MUTED};
`;

const Inner = styled.div`
    display: grid;
    gap: 40px;
    max-width: 1440px;
    margin: 0 auto;
    padding: 48px 24px 40px;

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: minmax(220px, 1.2fr) 3fr minmax(160px, 1fr);
        gap: 48px;
        padding: 64px 32px 48px;
    }
`;

const Brand = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const LogoLink = styled(Link)`
    display: block;
    width: 140px;
    height: 39px;
    ${focusRing}
`;

const Tagline = styled.p`
    margin: 0;
    max-width: 28ch;
    font-size: 14px;
    line-height: 1.5;
`;

const SocialList = styled.ul`
    display: flex;
    gap: 4px;
    margin: 0 0 0 -10px;
    padding: 0;
    list-style: none;
`;

const SocialLink = styled.a`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 12px;
    border-radius: 50%;
    color: ${TEXT_MUTED};
    transition: background-color .15s ease, color .15s ease;

    &:hover {
        background-color: #E4E4E7;
        color: ${colors.nightDark};
    }

    ${focusRing}
    &:focus-visible { border-radius: 50%; }
`;

const Nav = styled.nav`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px 24px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
`;

const Aside = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;

    @media screen and (min-width: ${breakpoints.sm}) and (max-width: calc(${breakpoints.lg} - 1px)) {
        flex-direction: row;
        flex-wrap: wrap;
        gap: 32px 48px;
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: ${colors.nightDark};
`;

const LinkList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const TextLink = styled.a`
    display: inline-block;
    padding: ${({ $small }) => ($small ? '6px 0' : '8px 0')};
    font-size: ${({ $small }) => ($small ? '13px' : '15px')};
    line-height: 1.4;
    color: ${TEXT_MUTED};
    text-decoration: none;
    overflow-wrap: anywhere;
    cursor: pointer;

    &:hover {
        color: ${colors.nightDark};
        text-decoration: underline;
        text-underline-offset: 3px;
    }

    ${focusRing}
`;

const BadgesSlot = styled.div`
    & > div {
        justify-content: flex-start;
    }
`;

const SealLink = styled.a`
    display: inline-flex;
    width: fit-content;

    & > img {
        display: block;
        height: auto;
    }

    ${focusRing}
`;

const Bottom = styled.div`
    background-color: ${SURFACE_BOTTOM};
    /* Espaço para a barra fixa de compra (FooterCheckout) no mobile. */
    padding-bottom: 80px;

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-bottom: 0;
    }
`;

const BottomInner = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-width: 1440px;
    margin: 0 auto;
    padding: 20px 24px;

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding: 20px 32px;
    }
`;

const Legal = styled.p`
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
`;

const LegalNav = styled.nav``;

const LegalList = styled.ul`
    display: flex;
    flex-wrap: wrap;
    gap: 0 20px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const VisuallyHidden = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`;
